// The host members that several feature packages ask for, built once. A package declares which it
// uses (`Pick<HostBase, ...>` in its `host.ts`) and the `register-<m>.tsx` / `<m>-host.tsx` file takes
// them with `pickHostBase(...)` instead of wiring `useAuth`, `trackFeatureAction`, `paths`... again.
//
// This file is imported by the eager `register-*` files, so it is in the entry chunk: it may import
// only what the entry already holds (auth, analytics events, paths, brand, service categories) and
// the sales dialog stays a lazy slot. `useSelfServeActivation` pulls the customer API, which the
// entry does not hold, so it lives in `./host-base-lazy` for the lazy hosts (checklist rule 7).
import { createElement, lazy, Suspense } from 'react';
import { useAuth } from '@/auth/AuthProvider';
import { brand } from '@/config/brand';
import { trackFeatureAction, trackServiceActivate } from '@/lib/analytics-events';
import { paths } from '@/lib/paths';
import { categoryBackTarget } from '@/lib/service-categories';
import type { HostBase } from '@vnetwork/margarita-shell/host-base';

// Lazy: the dialog renders only on an activation screen, so its code (leads API, contact query) is its own chunk.
const LazyServiceContactSalesDialog = lazy(() =>
  import('@/components/ServiceContactSalesDialog').then((m) => ({ default: m.ServiceContactSalesDialog })),
);
const ServiceContactSalesDialog: HostBase['ServiceContactSalesDialog'] = (props) =>
  createElement(Suspense, { fallback: null }, createElement(LazyServiceContactSalesDialog, props));

/** Every member of `HostBase` that is safe in the entry chunk. */
export const hostBase: Omit<HostBase, 'useSelfServeActivation'> = {
  trackFeatureAction,
  trackServiceActivate,
  useSession: useAuth,
  ServiceContactSalesDialog,
  categoryBackTarget,
  paths,
  brand,
};

/**
 * The host a package declared: the `HostBase` members it picked (`keys`) plus its own. A call, not an
 * object spread: the build transpiles a spread to an inline helper in every register file, and these
 * files are in the entry chunk.
 */
export function withHostBase<K extends keyof typeof hostBase, O extends object>(
  keys: readonly K[],
  own: O,
): Pick<typeof hostBase, K> & O {
  const picked = {} as Pick<typeof hostBase, K>;
  for (const key of keys) picked[key] = hostBase[key];
  return Object.assign(picked, own);
}
